import {
  always,
  applySpec,
  compose,
  flatten,
  map,
  prop,
  propOr
} from "ramda";

import qr from '../assets/qr-code.gif'
import logoSVG from '../assets/asm-group-logo.svgraw'
import jsPDF from '../lib/pdf'

const table = applySpec({
  head: prop('head'),
  body: prop('body'),
  styles: always({
    font: 'Roboto-Regular',
    fontStyle: 'normal',
    lineWidth: 1.1,
    lineColor: 200
  }),
  theme: always('grid'),
  margin: always(56),
  startY: prop('startY'),
});

const cel = compose(
  applySpec({
    content: propOr('', 'text'),
    styles: applySpec({
      textColor: propOr('#444', 'color'),
      fillColor: propOr(255, 'fillColor'),
      lineColor: propOr(200, 'lineColor'),
      halign: propOr(null, 'halign')
    }),
    colSpan: propOr(null, 'colSpan')
  })
);

const row = (...cols) => flatten(cols);


export default {
  methods: {
    $pdfHead(data) {
      return [
        [
          {
            content: this.$t('message.spendsBasedOn', [1]),
            colSpan: 4,
            styles: {halign: 'left', fillColor: [20, 90, 160], lineColor: [20, 90, 160]},
          },
          {
            content: this.$t('message.needsFor', { period: this.$tc('plural.day', data.period)}),
            colSpan: 2,
            styles: {halign: 'right', fillColor: [20, 90, 160], lineColor: [20, 90, 160]}
          }
        ],
        [
          cel({
            text: this.$t('message.component'),
            halign: 'left',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
          cel({
            text: this.$t('message.price'),
            halign: 'right',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
          cel({
            text: this.$t('message.quantity'),
            halign: 'right',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
          cel({
            text: this.$t('message.sum'),
            halign: 'right',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
          cel({
            text: this.$t('message.quantity'),
            halign: 'right',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
          cel({
            text: this.$t('message.sum'),
            halign: 'right',
            color: 0,
            fillColor: 200, lineColor: 200
          }),
        ]
      ];
    },
    $pdfBody(data) {
      return [
        ...map((ing) => row(
          cel({
            text: ing.label,
          }),
          cel({
            text: `${this.priceDisplay(ing.price.value * ing.purchase.multiplier)} ${data.currency}/${ing.purchase.measure}`,
            halign: 'right',
          }),
          cel({
            text: `${this.priceDisplay(ing.value.value, false)} ${ing.measure}`,
            halign: 'right',
          }),
          cel({
            text: `${this.priceDisplay(ing.sqPrice)} ${data.currency}`,
            halign: 'right',
          }),
          cel({
            text: `${this.priceDisplay(ing.amount / ing.purchase.multiplier, false)} ${ing.purchase.measure}`,
            halign: 'right',
            fillColor: 250
          }),
          cel({
            text: `${this.priceDisplay(ing.sum)} ${data.currency}`,
            halign: 'right',
            fillColor: 250
          })
        ))(data.ingredients),
        row(
          cel({
            text: this.$t('message.electricity'),
          }),
          cel({
            text: `${data.energy.price} ${data.currency}/${this.$t('measure.kW')}`,
            halign: 'right',
          }),
          cel({
            text: `${data.energy.value} ${this.$t('measure.kW')}`,
            halign: 'right',
          }),
          cel({
            text: `${data.energy.sqPrice} ${data.currency}`,
            halign: 'right',
          }),
          cel({
            text: `${data.energy.amount} ${this.$t('measure.kW')}`,
            margin: [16, 4, 16, 4],
            halign: 'right',
            fillColor: 250
          }),
          cel({
            text: `${data.energy.sum} ${data.currency}`,
            halign: 'right',
            margin: [16, 4, 16, 4],
            fillColor: 250,
          })
        ),
        row(
          cel({
            text: this.$t('message.rent'),
          }),
          cel({
            text: `${data.rent.price} ${data.currency}/${this.$t('measure.m2')}`,
            halign: 'right',
          }),
          cel({
            text: `${data.rent.value} ${this.$t('measure.m2')}`,
            halign: 'right',
          }),
          cel({
            text: `${data.rent.sqPrice} ${data.currency}`,
            halign: 'right',
          }),
          cel({
            text: ``,
            margin: [16, 4, 16, 4],
            fillColor: 250
          }),
          cel({
            text: `${data.rent.sum} ${data.currency}`,
            halign: 'right',
            margin: [16, 4, 16, 4],
            fillColor: 250
          })
        ),
        row(
          cel({
            text: this.$t('message.pieceRate'),
          }),
          cel({
            text: `${data.tariff.price} ${data.currency}/${this.$t('measure.m3')}`,
            halign: 'right',
          }),
          cel({
            text: ``,
            halign: 'right',
          }),
          cel({
            text: `${data.tariff.sqPrice} ${data.currency}`,
            halign: 'right',
          }),
          cel({
            text: ``,
            margin: [16, 4, 16, 4],
            fillColor: 250
          }),
          cel({
            text: `${data.tariff.sum} ${data.currency}`,
            halign: 'right',
            margin: [16, 4, 16, 4],
            fillColor: 250
          })
        ),
        row(
          cel({
            text: `${this.$t('message.costPriceOf', [this.$t('measure.Nm3', [1])])} : ${data.cost} ${data.currency}`,
            halign: 'right',
            colSpan: 4
          }),
          cel({
            text: `${data.sum} ${data.currency}`,
            halign: 'right',
            fillColor: 250,
            colSpan: 2
          }),
        )
      ];
    },

    async savePDF(data) {
      const doc = new jsPDF('p', 'pt', 'a4');
      // Document defaults
      doc.autoTableSetDefaults({
        headStyles: {fillColor: [20, 90, 160], lineColor: [20, 90, 160]},
        margin: {top: 0}
      });

      doc.addImage(qr, 'GIF', 460, 37, 85, 85);

      doc.addMySvgAsImage(logoSVG, 56, 51, 214, 57, 'logo', 'NONE');

      doc.setFont('Roboto-Regular');
      doc.setLineHeightFactor(1.5);

      doc.setFontSize(60);
      doc.setTextColor(20, 90, 160);
      doc.text(data.line.name, 56, 188, {maxWidth: 482});

      doc.setTextColor(0);
      doc.setFontSize(21);
      doc.setLineHeightFactor(1.1);
      doc.text(data.line.description, 56, 210, {maxWidth: 482});

      doc.setFontSize(14);
      doc.setLineHeightFactor(1.5);
      doc.text(this.$t('message.calculationOfProfitability', {
        line: data.line.name,
        period: this.$tc('plural.day', data.period),
        capability: this.$t('measure.Nm3', [data.capability])
      }), 56, 240, {maxWidth: 482})

      doc.autoTable(table({
        head: this.$pdfHead(data),
        body: this.$pdfBody(data),
        startY: 280,
      }));

      doc.autoTable(table({
        head: [
          [{content: this.$t('message.profitabilityBasedOn', [1]), colSpan: 3}],
          [
            cel({text: this.$t('message.sellPrice'), fillColor: 200, lineColor: 200, color: 0, halign: 'right'}),
            cel({text: this.$t('message.costPrice'), fillColor: 200, lineColor: 200, color: 0, halign: 'right'}),
            cel({text: this.$t('message.profit'), fillColor: 200, lineColor: 200, color: 0, halign: 'right'})
          ]
        ],
        body: [
          [
            cel({text: `${data.price} ${data.currency}`, halign: 'right'}),
            cel({text: `${data.cost} ${data.currency}`, halign: 'right'}),
            cel({text: `${data.profit} ${data.currency}`, halign: 'right'})
          ]
        ],
        startY: doc.previousAutoTable.finalY + 28,
      }));

      doc.setFontSize(14);
      doc.text(this.$t('message.profitabilityBasedOn', [data.capability * data.period]), 538, doc.previousAutoTable.finalY + 24, {
        maxWidth: 482,
        align: 'right'
      });


      doc.setFontSize(24);
      doc.text(`${data.fullProfit} ${data.currency}`, 538, doc.previousAutoTable.finalY + 48, {
        maxWidth: 482,
        align: 'right'
      });


      doc.setFontSize(8);
      let year = (new Date()).getFullYear();
      doc.text(this.$t('message.copyright', {year}), 56, 780, {maxWidth: 482, align: 'left'});
      doc.text(this.$t('message.addressRegion'), 56, 793, {maxWidth: 482, align: 'left'});
      doc.text(this.$t('message.addressStreet'), 56, 806, {maxWidth: 482, align: 'left'});

      doc.text(`${this.$t('message.site')}: asm.ru     Email: info@asm.ru`, 538, 780, {maxWidth: 482, align: 'right'});
      doc.text(`WhatsApp, Viber, Telegram: +7 (923) 004-5555`, 538, 793, {maxWidth: 482, align: 'right'});
      doc.text(`${this.$t('message.international')}: +7 (3852) 500-545`, 538, 806, {maxWidth: 482, align: 'right'});


      const filename = `${this.$t('message.profitabilityCalculationOf')} ${data.line.name}.pdf`;

      doc.setProperties({
        title: filename,
        author: 'ASM-GROUP',
        creator: 'ASM-GROUP',
        filename
      });

      // If IE
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(doc.output("blob", filename), filename);
      } else {

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const url = window.URL.createObjectURL(doc.output("blob", filename));
        doc.save(filename);

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      }
      // doc.save(filename)
    }
  }
}
